import { getReadableFileSize, isFiniteNumber } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useState } from "react";
import { Button } from "../../../Button";
import { TextDataDisplay } from "../../../DataDisplay/TextDataDisplay";
import { Icon } from "../../../Icon";
import { FilePreview } from "../FilePreview";
import type { FileCardProps } from "../types";
import styles from "./FileCard.module.scss";

const cx = classNames.bind(styles);

const FileCard = ({
  file,
  percentageComplete,
  percentageLabel,
  isLoading,
  isBusy,
  isDisabled,
  isValid,
  errorMessage,
  isDeletable = true,
  onChange,
  isCancellable = true,
  onCancel,
}: FileCardProps) => {
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const isPercentageSet = isFiniteNumber(percentageComplete ?? "");

  const handleDelete = () => {
    if (isDisabled || isBusy) return;

    onChange?.(null, file);
  };

  const handleCancel = () => {
    if (isDisabled || isBusy) return;

    if (file) onCancel?.(file);
  };

  const fileSize = file ? getReadableFileSize(file.size) : null;
  const fileName = file ? file.name : null;

  const inProgress =
    isPercentageSet && percentageComplete !== 100 && !errorMessage;
  const isComplete =
    isPercentageSet && percentageComplete === 100 && !errorMessage;
  const isReady =
    isPercentageSet &&
    percentageComplete! >= 100 &&
    isPreviewReady &&
    !errorMessage;

  const details = inProgress ? (percentageLabel ?? "") : (fileSize ?? "");

  return (
    <div
      className={cx("file-container", {
        invalid: !isValid,
      })}
    >
      <FilePreview
        file={file}
        isLoading={isLoading}
        isBusy={inProgress || isBusy}
        isValid={isValid}
        isReady={isPreviewReady}
        onReady={() => setIsPreviewReady(true)}
      />

      <div className={cx("file-information-container")}>
        {fileName ? (
          <div className={cx("filename-container")}>
            <TextDataDisplay
              className={cx("filename")}
              value={fileName}
              clamp={1}
            />
            {isReady ? (
              <Icon
                variant="check-circle"
                size={16}
                weight="fill"
                className={cx("check-circle-icon")}
                aria-label={percentageLabel ?? undefined}
              />
            ) : null}
          </div>
        ) : null}
        {inProgress ? (
          <div className={cx("progress-bar")}>
            <span
              className={cx("progress-bar-fill")}
              style={{
                width: `${percentageComplete ?? 1}%`,
              }}
            ></span>
          </div>
        ) : null}
        {details ? <p className={cx("file-meta")}>{details}</p> : null}
        {errorMessage ? (
          <p className={cx("file-meta", "error")}>{errorMessage}</p>
        ) : null}
      </div>

      {isDeletable || isCancellable ? (
        <div className={cx("close-icon-container")}>
          {isDeletable && (isComplete || errorMessage) ? (
            <Button
              variant="outline"
              icon="trash"
              size="xx-small"
              iconProps={{ size: 24, weight: "light" }}
              disabled={isBusy}
              onClick={handleDelete}
              title={fileName ? `Delete ${fileName}` : "Delete file"}
            />
          ) : null}
          {isCancellable && inProgress ? (
            <Button
              className={cx("close-button")}
              icon="x-circle"
              size="xx-small"
              iconProps={{ size: 24, weight: "light" }}
              disabled={isBusy}
              onClick={handleCancel}
              title={
                fileName ? `Cancel upload of ${fileName}` : "Cancel upload"
              }
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default FileCard;
